import { AbstractSceneTransition } from "slotworks/graphics/layers/scene-transitions/abstract-scene-transition";
import Contract from "slotworks/utils/contract";
import * as TweenJS from "slotworks/utils/tween";
import { FadeIn, FadeOut, PivotBounceZoomIn, PivotBounceZoomOut } from "slotworks/graphics/animation/base-animations";
import Layers from "slotworks/graphics/layers/layers";
import { sequence, parallel } from "slotworks/utils/race-utils";
import * as particles from "pixi-particles";
import particleManager from "slotworks/graphics/particles/particle-manager";
import Point from "slotworks/utils/geom/point";
import deviceInfo from "slotworks/utils/device-info";
import canvasManager, { Orientation } from "slotworks/graphics/canvas/canvas-manager";
import soundManager from "slotworks/sound/sound-manager";
import settingsModel from "slotworks/model/settings/settings-model";

export default class FreespinsPlaqueTransitions extends AbstractSceneTransition {

    private container: PIXI.Container;
    private glowStack: PIXI.Sprite[];
    private tweens: TweenJS.Tween[];

    private particleContainer: PIXI.Container;
    private dustParticle: particles.Emitter;
    private dustParticleId: number;
    private starsParticle: particles.Emitter;
    private starsParticleId: number;

    public in(): Contract<void> {
        this.init();
        return parallel([
            () => FadeIn(Layers.CelebrationBackground.getSprite("winrect"), 400),
            () => this.initStacks(),
            () => PivotBounceZoomIn(Layers.Prompts.container, 400, new Point(0.5, 0.5), 100)
        ])
    }

    public out(): Contract<void> {
        soundManager.customEvent("plaque_exit");
        this.stopParticles(); 
        PivotBounceZoomOut(Layers.Prompts.container, 400, new Point(0.5, 0.5)).then(() => {
            this.clean();
            this.clearParticles();
            Layers.Prompts.container.children.forEach(el => el.visible = false);            
        })
        return parallel([
            () => this.zoomOutStacks(),
            () => FadeOut(Layers.CelebrationBackground.getSprite("winrect"), 500)
        ]);
    }

    protected updatePositions = () => {
        const background = Layers.CelebrationBackground.getSprite("winrect");
        background.width = canvasManager.stageWidth;            
        background.x = -canvasManager.stageOffsetX;
        if(canvasManager.orientation == Orientation.PORTRAIT) {
            background.height = canvasManager.stageHeight * 1.43;
            background.y = -(canvasManager.stageOffsetY *2 - Layers.CelebrationBackground.container.y);
        } else {
            background.height = canvasManager.stageHeight;
            background.y = -canvasManager.stageOffsetY;
        }
        this.setMask();
    }

    private clean() {
        if(!deviceInfo.isDesktop) {
            canvasManager.onOrientationChange.remove(this.updatePositions);
        }
    }

    private setMask() {
        const maskPosition = Layers.Prompts.getPosition("mask");
        const mask = (this.particleContainer.mask as PIXI.Graphics) || new PIXI.Graphics();
        mask.clear();        
        mask.drawRect(Layers.Prompts.container.x + maskPosition.x + canvasManager.stageOffsetX, 
            Layers.Prompts.container.y + maskPosition.y + canvasManager.stageOffsetY, maskPosition.width, maskPosition.height);
        this.particleContainer.mask = mask;
    }

    private init() {     
        const title = Layers.Prompts.getSprite("288_title_freespins_Large")
        const cntitle = Layers.Prompts.getSprite("288_title_freespins_Large_chinese");  
        if(settingsModel.language == "zh-Hans") {
            if(title) title.visible = false;
        } else {
            if(cntitle) cntitle.visible = false;
        }
        
        this.particleContainer = new PIXI.Container();
        Layers.Prompts.container.addChildAt(this.particleContainer, 1);
        const position = Layers.Prompts.getPosition("dust");
        const maskPosition = Layers.Prompts.getPosition("mask");
        this.dustParticleId = particleManager.add("dust", new Point(position.x, position.y), this.particleContainer);
        this.dustParticle = particleManager.get(this.dustParticleId);
        this.dustParticle.emit = true;
        this.starsParticleId = particleManager.add("stars", new Point(maskPosition.x, maskPosition.y), this.particleContainer);
        this.starsParticle = particleManager.get(this.starsParticleId);
        this.starsParticle.emit = true;
        if(!deviceInfo.isDesktop) {
            this.updatePositions();
            canvasManager.onOrientationChange.add(this.updatePositions);
        } else {
            this.setMask();
        }
    }

    private stopParticles() {
        this.dustParticle.emit = false;
        this.starsParticle.emit = false;
    }

    private clearParticles() {        
        particleManager.remove(this.dustParticleId);
        particleManager.remove(this.starsParticleId);
        this.particleContainer.mask = null;
        Layers.Prompts.container.removeChild(this.particleContainer);
        this.particleContainer.destroy({ children: true});
        this.particleContainer = null;
        this.dustParticle = null;
        this.starsParticle = null;
    }

    private initStacks() {
        this.container = new PIXI.Container();
        Layers.CelebrationBackground.container.addChildAt(this.container, 1);
        this.glowStack = [];  
        this.glowStack.push(Layers.CelebrationBackground.getSprite("shine"));  
        this.glowStack.push(Layers.CelebrationBackground.getSprite("star2"));  
        this.glowStack.push(Layers.CelebrationBackground.getSprite("star1")); 
        this.glowStack.push(Layers.CelebrationBackground.getSprite("star0")); 
        this.container.position.set(
            this.glowStack[0].x + this.glowStack[0].width/2,
            this.glowStack[0].y + this.glowStack[0].height/2
        );
        for(let i = this.glowStack.length - 1; i >= 0; i--) {
            this.glowStack[i].anchor.set(0.5, 0.5);
            this.glowStack[i].position.set(0, 0);
            this.container.addChild(this.glowStack[i]);
        }
        this.glowStack[0].scale.set(1.22, 1.22);
        this.glowStack[1].scale.set(1.22, 1.22);
        this.glowStack[2].scale.set(1.22, 1.22);
        this.glowStack[3].scale.set(1.22, 1.22);

        this.tweens = [];
        this.tweens.push(
            new TweenJS.Tween(this.glowStack[0])
            .to({ rotation: Math.PI * 2 }, 40000)
            .repeat(Infinity)
            .start());
        
            this.tweens.push(
            new TweenJS.Tween(this.glowStack[1])
            .to({ rotation: Math.PI * 2 }, 25000)
            .repeat(Infinity)
            .start());
        
            this.tweens.push(
            new TweenJS.Tween(this.glowStack[2])
            .to({ rotation: Math.PI * 2 }, 30000)
            .repeat(Infinity)
            .start());
            this.tweens.push(
            new TweenJS.Tween(this.glowStack[3])
            .to({ rotation: Math.PI * 2 }, -25000)
            .repeat(Infinity)
            .start());

        this.container.scale.set(0, 0);
        return new Contract(resolve => {
            new TweenJS.Tween(this.container.scale)
            .to({x: 1, y: 1}, 200)
            .chain(
                new TweenJS.Tween(this.container.scale)
                .to({ x: 0.9, y: 0.9}, 200)
                .onComplete(resolve))            
            .start();
        });
    }

    private zoomOutStacks() {
        return sequence([
            () => new Contract(resolve => {
                new TweenJS.Tween(this.container.scale)
                .to({x: 1.5, y: 1.5}, 200)
                .delay(80)
                .onComplete(() => {
                    new TweenJS.Tween(this.container.scale)
                    .to({x: 0, y: 0}, 300)
                    .delay(80)
                    .onComplete(() => {
                        this.tweens.forEach(tween => {
                            tween.stop();
                        });
                        this.tweens = [];
                        this.glowStack.forEach(element => {
                            Layers.CelebrationBackground.container.addChild(element);
                            element.visible = false;
                        });
                        resolve(null);
                    }).start();       
                })
                .start();         
            })
        ]);
    }

}


// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./src/transitions/freespins-plaque-transitions.ts